/* eslint-disable react/jsx-newline */
import React from 'react';
import { RouteObject } from 'react-router-dom';
import { load } from 'core/utils/DynamicPreloading';
import { getRootUrl } from 'core/window/bootstrapConfig';
import { PageTypes } from 'core/constant/analytics/PageTypes';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { ActivitiesPageWrapper } from 'app/ActivitiesPageWrapper';
import PageNotFoundErrorPage from 'component/page/error/PageNotFoundErrorPage';

const HomePage = React.lazy(load(() => import(/* webpackPrefetch: true */ 'component/page/home/HomePage')));
const SearchPage = React.lazy(load(() => import(/* webpackPrefetch: true */ 'component/page/search/Search/SearchPage')));
const CityPage = React.lazy(load(() => import(/* webpackPrefetch: true */ 'component/page/search/City/CityPage')));
const DetailsPage = React.lazy(load(() => import(/* webpackPrefetch: true */ 'component/page/detail/DetailsPage')));

const activitiesRoute = (config: BootstrapConfig, page: React.ReactNode, pageType: PageTypes, path: string): RouteObject => {
    return {
        path,
        element: <ActivitiesPageWrapper config={config} page={page} pageType={pageType} />,
    };
};

const ActivitiesRoutes = (config: BootstrapConfig): RouteObject[] => {
    const rootUrl = getRootUrl(config);
    const rootUrlWithActivities = rootUrl?.includes('/activities') ? rootUrl : `/activities${rootUrl}`;

    // React router V6 does not support optional Segment in path
    return [
        // Home page routes
        activitiesRoute(config, <HomePage />, PageTypes.Home, `${rootUrl}`),
        activitiesRoute(config, <HomePage />, PageTypes.Home, `/:culture${rootUrl}`),

        // Nearby page routes
        activitiesRoute(config, <HomePage />, PageTypes.Home, `${rootUrl}/nearby`),
        activitiesRoute(config, <HomePage />, PageTypes.Home, `/:culture${rootUrl}/nearby`),

        // Search page routes
        activitiesRoute(config, <SearchPage />, PageTypes.Search, `${rootUrl}/search`),
        activitiesRoute(config, <SearchPage />, PageTypes.Search, `/:culture${rootUrl}/search`),

        // City page routes
        activitiesRoute(config, <CityPage />, PageTypes.City, `${rootUrl}/city/:country_name/:city_name`),
        activitiesRoute(config, <CityPage />, PageTypes.City, `/:culture${rootUrl}/city/:country_name/:city_name`),

        activitiesRoute(config, <CityPage />, PageTypes.City, `${rootUrl}/area/:country_name/:city_name/:area_name`),
        activitiesRoute(config, <CityPage />, PageTypes.City, `/:culture${rootUrl}/area/:country_name/:city_name/:area_name`),
        activitiesRoute(config, <CityPage />, PageTypes.City, `${rootUrl}/region/:country_name/:state_name`),
        activitiesRoute(config, <CityPage />, PageTypes.City, `/:culture${rootUrl}/region/:country_name/:state_name`),
        activitiesRoute(config, <CityPage />, PageTypes.City, `${rootUrl}/country/:country_name`),
        activitiesRoute(config, <CityPage />, PageTypes.City, `/:culture${rootUrl}/country/:country_name`),

        // Detail page routes
        activitiesRoute(config, <DetailsPage />, PageTypes.Details, `${rootUrl}/detail`),
        activitiesRoute(config, <DetailsPage />, PageTypes.Details, `/:culture${rootUrl}/detail`),
        activitiesRoute(config, <DetailsPage />, PageTypes.Details, `${rootUrl}/detail/:country_name/:city_name/:activity_name`),
        activitiesRoute(config, <DetailsPage />, PageTypes.Details, `/:culture${rootUrl}/detail/:country_name/:city_name/:activity_name`),

        // Page Not Found page routes
        activitiesRoute(config, <PageNotFoundErrorPage />, PageTypes.PageNotFound, `${rootUrlWithActivities}/pagenotfound`),
        activitiesRoute(config, <PageNotFoundErrorPage />, PageTypes.PageNotFound, `/:culture${rootUrlWithActivities}/pagenotfound`),

        // Fallback to home page
        activitiesRoute(config, <HomePage />, PageTypes.Home, '*'),
    ];
};

export default ActivitiesRoutes;
